/**
 * Turning URLs in a message body into something clickable.
 *
 * The body is split into plain and linked segments and rendered with `*ngFor`, **not** handed to
 * `[innerHTML]`. Message bodies are attacker-controlled — anyone in the office can type one — so
 * building an HTML string here would mean either trusting `DomSanitizer.bypassSecurityTrust*` on
 * user input or relying on Angular's sanitiser to catch everything. Segments avoid the question
 * entirely: the text stays text, and only the `href` of an anchor we construct is ever a URL.
 */

/** One run of a message body: plain text, or text that is also a link. */
export interface ChatBodySegment {
  text: string;
  /** Absolute URL to open, or null for a plain run. */
  href: string | null;
}

/**
 * `http(s)://…`, plus bare `www.…` which people type constantly and which is unambiguous enough
 * to be worth catching. Stops at whitespace and at `<` so a pasted tag can't be swallowed.
 */
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+)/gi;

/**
 * Trailing punctuation belongs to the sentence, not the URL — "see http://x.com." must not 404.
 *
 * `>` is in the set because the pattern above stops at `<` but has no reason to stop at `>`, so a
 * link written the common `<https://example.com>` way would otherwise keep the closing bracket.
 */
const TRAILING_PUNCTUATION = /[.,;:!?)\]}>'"]+$/;

/**
 * Split `body` into renderable segments. Always returns at least one segment for a non-empty body.
 */
export function linkifyBody(body: string | null | undefined): ChatBodySegment[] {
  const text = body ?? '';
  if (!text) {
    return [];
  }

  const segments: ChatBodySegment[] = [];
  let lastIndex = 0;

  // `matchAll` rather than a `while (exec())` loop: the regex is module-level and global, so a
  // manual loop would carry `lastIndex` between calls and silently skip links on every other body.
  for (const match of text.matchAll(URL_PATTERN)) {
    const raw = match[0];
    const start = match.index ?? 0;

    const trimmed = raw.replace(TRAILING_PUNCTUATION, '');
    // A "URL" that is nothing but punctuation after trimming is not one.
    if (!trimmed || !/[a-z0-9]/i.test(trimmed)) {
      continue;
    }

    if (start > lastIndex) {
      segments.push({ text: text.slice(lastIndex, start), href: null });
    }

    segments.push({ text: trimmed, href: hrefFor(trimmed) });
    lastIndex = start + trimmed.length;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), href: null });
  }

  return segments.length ? segments : [{ text, href: null }];
}

/** True when the body has at least one link, so the bubble can skip the segment renderer. */
export function hasLink(body: string | null | undefined): boolean {
  return linkifyBody(body).some(segment => !!segment.href);
}

/**
 * A `www.` link has no scheme, and an `href` without one is resolved as a *relative* path — which
 * would navigate the app to `/chat/www.example.com` instead of leaving the site.
 */
function hrefFor(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
